// What a schedule activity IS, on two axes - the browser side of
// back/utils/activityType.js. Keep the two in step.
//
//   type  - how the row BEHAVES:
//           task | summary | start_ms | finish_ms | loe | hammock | meeting
//   kind  - what it MEANS: 'signoff', 'area_release', 'ntp',
//           'substantial_completion' ... a key from the company's
//           milestone_kind list.
//
// One enum used to carry both, which is why there was no level of effort,
// no explicit summary and no way to say a diamond was a Notice To Proceed
// rather than a BIM sign-off.
//
// The retired values (milestone / signoff / area_release) still arrive
// from unmigrated rows and older API responses, so everything that reads
// a type goes through `normalizeType` and nothing compares a raw string.

export const ACTIVITY_TYPES = [
  { value: "task", label: "Task", hint: "Dated work with a duration" },
  { value: "summary", label: "Summary", hint: "Spans its children; no duration of its own" },
  { value: "start_ms", label: "Start milestone", hint: "A single date that opens something" },
  { value: "finish_ms", label: "Finish milestone", hint: "A single date that completes something" },
  { value: "loe", label: "Level of effort", hint: "Ongoing support: PM, supervision, general conditions" },
  { value: "hammock", label: "Hammock", hint: "Spans a range of other activities" },
  { value: "meeting", label: "Meeting", hint: "Recurring cadence, drawn once per occurrence" },
];

// Single-date rows: start === finish, no duration to enter.
export const ZERO_DURATION_TYPES = ["start_ms", "finish_ms", "meeting"];

// Drawn as a diamond rather than a bar.
export const MILESTONE_TYPES = ["start_ms", "finish_ms"];

// The old enum -> [type, implied kind].
export const LEGACY_TYPES = {
  milestone: ["finish_ms", ""],
  signoff: ["finish_ms", "signoff"],
  area_release: ["start_ms", "area_release"],
};

const KNOWN = ACTIVITY_TYPES.map((t) => t.value);

export function normalizeType(type, kind) {
  const t = String(type || "").trim();
  const k = String(kind || "").trim();
  if (LEGACY_TYPES[t]) {
    const [mapped, implied] = LEGACY_TYPES[t];
    return { type: mapped, kind: k || implied };
  }
  if (KNOWN.indexOf(t) >= 0) return { type: t, kind: k };
  return { type: "task", kind: k };
}

export const typeOf = (item) => normalizeType(item && item.type, item && item.kind).type;
export const isZeroDuration = (type) => ZERO_DURATION_TYPES.indexOf(normalizeType(type).type) >= 0;
export const isMilestone = (type) => MILESTONE_TYPES.indexOf(normalizeType(type).type) >= 0;
export const isSummaryType = (type) => normalizeType(type).type === "summary";
export const isMeeting = (type) => normalizeType(type).type === "meeting";

export const typeLabel = (type) => {
  const t = normalizeType(type).type;
  const found = ACTIVITY_TYPES.find((x) => x.value === t);
  return found ? found.label : t;
};
